import { useCallback, useEffect, useState } from "react";
import { TShirt, Sparkle, Globe, Plus, Trash, TrendUp, TrendDown, Image as ImageIcon, Storefront, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ResearchAnswer, ResearchKeyPanel } from "@/components/Research";
import { ShareBar } from "@/components/ShareBar";
import { CastingBoard, CastingStrip } from "@/components/CastingBoard";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`);

export default function Fashion() {
  const { user } = useAuth();
  const [view, setView] = useState("board");
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [read, setRead] = useState(null);
  const [engine, setEngine] = useState("gpt");
  const [picEngine, setPicEngine] = useState("gemini");
  const [busy, setBusy] = useState(false);
  const [picture, setPicture] = useState(null);
  const [picPrompt, setPicPrompt] = useState("");
  const [research, setResearch] = useState(null);
  const [segment, setSegment] = useState("workwear");
  const [sell, setSell] = useState({ title: "", price: "", quantity: 1, unit: "pcs", segment: "workwear", currency: "USD" });
  const [source, setSource] = useState({ title: "", quantity: 1, unit: "pcs", budget: "", segment: "workwear", currency: "USD" });
  const [form, setForm] = useState({ title: "", body: "", segment: "workwear", public: true });

  const load = useCallback(() => {
    api.get("/fashion/board").then(({ data: d }) => setData(d)).catch(oops);
    api.get("/fashion/rates").then(({ data: d }) => setRates(d)).catch(() => setRates(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const makePicture = async (prompt) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/fashion/picture", {
        prompt: (prompt ?? picPrompt).trim() || null, segment, engine: picEngine });
      setPicture(d);
      toast.success(`Picture made by ${d.engine_label}`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const refreshPictures = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/fashion/pictures/refresh");
      toast.success(`Fresh pictures — ${d.notes} note(s), ${d.listings} advert(s)`);
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const toggleAuto = async (id, on) => {
    try {
      await api.post(`/fashion/notes/${id}/auto-picture`, { every_5m: on });
      toast.success(on ? "This note gets a fresh picture every 5 minutes" : "Five-minute refresh off");
      load();
    } catch (e) { oops(e); }
  };

  const notePicture = async (id) => {    setBusy(true);
    try {
      await api.post(`/fashion/notes/${id}/picture`, { engine: picEngine });
      toast.success("Picture added to the note");
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const listingPicture = async (id) => {
    setBusy(true);
    try {
      await api.post(`/fashion/listings/${id}/picture`, { engine: picEngine });
      toast.success("Picture added to the advert");
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const listForSale = async () => {
    try {
      const { data: d } = await api.post("/fashion/sell", {
        title: sell.title.trim(), segment: sell.segment, price: Number(sell.price),
        quantity: Number(sell.quantity), unit: sell.unit,
        currency: sell.currency.toLowerCase(),
        image_id: picture?.image_id || null });
      toast.success(`"${d.title}" is live in the marketplace at ${d.currency.toUpperCase()} ${d.price}`);
      setSell({ ...sell, title: "", price: "" });
      load();
    } catch (e) { oops(e); }
  };

  const askSuppliers = async () => {
    try {
      const { data: d } = await api.post("/fashion/source", {
        title: source.title.trim(), segment: source.segment,
        quantity: Number(source.quantity), unit: source.unit,
        currency: source.currency.toLowerCase(),
        budget: Number(source.budget || 0) });
      toast.success(`Request "${d.title}" is open for supplier quotes`);
      setSource({ ...source, title: "", budget: "" });
    } catch (e) { oops(e); }
  };

  const saveMargin = async (value) => {
    try {
      const { data: d } = await api.post("/fashion/rates/margin", { margin_pct: Number(value) });
      setRates(d);
      toast.success(`Margin set to ${d.margin_pct}%`);
    } catch (e) { oops(e); }
  };

  const askRead = async (which = engine) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/fashion/read?engine=${which}`, {});
      setRead(d);
      setEngine(which);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const askResearch = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/fashion/research", { segment });
      setResearch(d);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const addNote = async () => {
    try {
      await api.post("/fashion/notes", form);
      toast.success("Trend note saved");
      setForm({ ...form, title: "", body: "" });
      load();
    } catch (e) { oops(e); }
  };

  const removeNote = async (id) => {
    try {
      await api.delete(`/fashion/notes/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { oops(e); }
  };

  if (!data) return <p className="py-12 text-center text-sm text-slate-500">Loading the trend board…</p>;

  return (
    <div className="space-y-6" data-testid="fashion-page">
      <PageHeader
        overline="Textiles & apparel"
        title="Fashion trends and what to stock"
        description="What is moving through your own orders, where the season is heading, and a read on what to buy more or less of."
      />

      <div className="flex flex-wrap items-center gap-3 border-l-2 border-[#B3241B] bg-[#FFF6E5] px-4 py-3">
        <TShirt size={22} className="text-[#B3241B]" weight="duotone" />
        <div>
          <p className="text-sm font-semibold text-[#0F172A]" data-testid="fashion-season">{data.season_label} season</p>
          <p className="text-xs text-slate-600">{data.buying_note}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {data.can_edit && (
          <Button variant="outline" className="w-full sm:w-auto" data-testid="fashion-read-btn" onClick={() => askRead()} disabled={busy}>
            <Sparkle size={16} className="mr-1.5" /> {busy ? "Reading…" : "What should we stock?"}
          </Button>
        )}
        {data.can_edit && (
          <>
            <select data-testid="fashion-research-segment" className="w-full border border-border bg-white px-2 py-2 text-base sm:w-auto md:text-sm"
              value={segment} onChange={(e) => setSegment(e.target.value)}>
              {data.segment_options.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
            </select>
            <Button variant="outline" className="w-full sm:w-auto" data-testid="fashion-research-btn" onClick={askResearch} disabled={busy}>
              <Globe size={16} className="mr-1.5" /> Research {LABEL(segment).toLowerCase()} on the live web
            </Button>
          </>
        )}
        {user?.role === "admin" && (
          <Button variant="outline" className="w-full sm:w-auto" data-testid="fashion-refresh-pictures" onClick={refreshPictures} disabled={busy}>
            <ImageIcon size={16} className="mr-1.5" /> {busy ? "Drawing…" : "Fresh pictures for today"}
          </Button>
        )}
      </div>

      {data.can_edit && !data.research_ready && (
        <ResearchKeyPanel onReady={load} />
      )}

      {read && (
        <div data-testid="fashion-read" className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3">
          <p className="text-sm leading-relaxed text-[#0F172A]">{read.summary}</p>
          {read.picks?.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {read.picks.map((p, i) => <li key={i} data-testid={`fashion-pick-${i}`}>· {p}</li>)}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#C7D6FF] pt-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Written by</span>
            {[["gpt", "GPT-5.5"], ["claude", "Claude Sonnet 4.6"]].map(([key, label]) => (
              <button key={key} data-testid={`fashion-engine-${key}`} disabled={busy} onClick={() => askRead(key)}
                className={`tap-safe border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 disabled:opacity-60 ${
                  (read.engine || engine) === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {research && <ResearchAnswer result={research} testid="fashion-research-answer" />}

      <Tabs value={view} onValueChange={setView} className="w-full scroll-x overflow-x-auto">
        <TabsList data-testid="fashion-tabs" className="w-max">
          <TabsTrigger value="board" data-testid="fashion-tab-board">Our movers</TabsTrigger>
          <TabsTrigger value="pictures" data-testid="fashion-tab-pictures">Trend pictures</TabsTrigger>
          <TabsTrigger value="rates" data-testid="fashion-tab-rates">Rate guide</TabsTrigger>
          <TabsTrigger value="sell" data-testid="fashion-tab-sell">Buy &amp; sell</TabsTrigger>
          <TabsTrigger value="notes" data-testid="fashion-tab-notes">Trend notes</TabsTrigger>
          {(user?.role === "admin" || user?.role === "manager") && (
            <TabsTrigger value="casting" data-testid="fashion-tab-casting">Casting</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {view === "pictures" && (
        <div className="space-y-4" data-testid="fashion-pictures">
          {!data.can_edit ? (
            <EmptyState testid="fashion-pictures-locked" title="Pictures are for buying accounts"
              description="Admin, vendor and buyer accounts can make trend pictures." />
          ) : (
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Make a trend picture</p>
            <p className="mt-1 text-xs text-slate-600">
              No photo needed — describe it, or just pick a segment and let the season fill in the rest.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>What should it show?</Label>
                <Input data-testid="fashion-pic-prompt" value={picPrompt} onChange={(e) => setPicPrompt(e.target.value)}
                  placeholder="Insulated hi-vis softshell jacket, navy and orange, on a hanger" />
              </div>
              <div>
                <Label>Segment</Label>
                <select data-testid="fashion-pic-segment" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                  value={segment} onChange={(e) => setSegment(e.target.value)}>
                  {data.segment_options.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
                </select>
              </div>
              <div>
                <Label>Picture engine</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {[["gemini", "Gemini Nano Banana"], ["openai", "GPT Image 1"]].map(([key, label]) => (
                    <button key={key} data-testid={`fashion-pic-engine-${key}`} onClick={() => setPicEngine(key)}
                      className={`tap-safe flex-1 border px-2.5 py-2 text-xs font-semibold transition-colors duration-150 sm:flex-none ${
                        picEngine === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                          : "border-border bg-white text-slate-600 hover:border-[#0F172A]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button className="mt-3 w-full sm:w-auto" data-testid="fashion-pic-make" onClick={() => makePicture()} disabled={busy}>
              <ImageIcon size={16} className="mr-1.5" /> {busy ? "Drawing…" : "Make the picture"}
            </Button>
          </div>
          )}

          {picture && (
            <div className="border border-border bg-white p-4" data-testid="fashion-picture-result">
              <img src={`${BACKEND}${picture.url}`} alt={picture.prompt}
                className="w-full max-w-md border border-border" data-testid="fashion-picture-img" />
              <p className="mt-2 text-xs text-slate-500">{picture.engine_label} · {picture.model}</p>
              <p className="mt-2 text-xs text-slate-600">
                Use it on an advert in the <button className="tap-safe font-semibold text-[#002FA7] underline" onClick={() => setView("sell")}>Buy &amp; sell</button> tab.
              </p>
            </div>
          )}
        </div>
      )}

      {view === "rates" && (
        <div className="space-y-4" data-testid="fashion-rates">
          {user?.role === "admin" && (
            <div className="flex flex-wrap items-end gap-3 border border-border bg-white p-4">
            <div>
              <Label>Your margin</Label>
              <Input data-testid="fashion-margin-input" type="number" className="mt-1 sm:max-w-[8rem]"
                defaultValue={rates?.margin_pct ?? 35}
                onBlur={(e) => saveMargin(e.target.value)} />
            </div>
            <p className="text-xs text-slate-600">
              Sell prices below are what you paid plus this margin. Change it and the guide updates.
            </p>
          </div>
          )}
          {!rates?.rates?.length ? (
            <EmptyState testid="fashion-rates-empty" title="No costs to work from yet"
              description={data.can_edit
                ? "Once apparel appears on a purchase order, this shows what you pay per segment and what to sell it at."
                : "The rate guide is for admin, vendor and buyer accounts."} />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fashion-rate-cards">
              {rates.rates.map((r, i) => (
                <div key={r.segment} data-testid={`fashion-rate-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{LABEL(r.segment)}</p>
                  <div className="mt-3 flex items-baseline justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">You pay</p>
                      <p className="text-lg font-bold text-[#0F172A]">{r.currency.toUpperCase()} {r.cost}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] uppercase tracking-wide text-slate-500">Sell at</p>
                      <p className="text-lg font-bold text-[#0B5C2E]" data-testid={`fashion-sell-price-${i}`}>{r.currency.toUpperCase()} {r.sell}</p>
                    </div>
                  </div>
                  {r.products?.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t border-border pt-2 text-xs text-slate-600">
                      {r.products.map((p) => (
                        <li key={p.name} className="flex justify-between gap-2">
                          <span>{p.name}</span>
                          <span className="shrink-0 font-semibold">{r.currency.toUpperCase()} {p.cost} → {p.sell}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "sell" && (
        <div className="space-y-4" data-testid="fashion-sell">
          {data.can_sell && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">List it for sale</p>
              <p className="mt-1 text-xs text-slate-600">
                Goes live in your marketplace{picture ? " with the picture you just made" : ""}, and on the public trends page with a Buy button.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div><Label>What are you selling?</Label><Input data-testid="fashion-sell-title" value={sell.title} onChange={(e) => setSell({ ...sell, title: e.target.value })} placeholder="Insulated hi-vis softshell jacket" /></div>
                <div>
                  <Label>Segment</Label>
                  <select data-testid="fashion-sell-segment" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                    value={sell.segment} onChange={(e) => setSell({ ...sell, segment: e.target.value })}>
                    {data.segment_options.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
                  </select>
                </div>
                <div><Label>Price each</Label><Input data-testid="fashion-sell-price" type="number" value={sell.price} onChange={(e) => setSell({ ...sell, price: e.target.value })} /></div>
                <div>
                  <Label>Currency</Label>
                  <select data-testid="fashion-sell-currency" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                    value={sell.currency} onChange={(e) => setSell({ ...sell, currency: e.target.value })}>
                    {(data.currencies || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
                  </select>
                </div>
                <div><Label>How many</Label><Input data-testid="fashion-sell-qty" type="number" value={sell.quantity} onChange={(e) => setSell({ ...sell, quantity: e.target.value })} /></div>
              </div>
              <Button className="mt-3 w-full sm:w-auto" data-testid="fashion-sell-submit" onClick={listForSale}
                disabled={sell.title.trim().length < 3 || !(Number(sell.price) > 0)}>
                <Storefront size={16} className="mr-1.5" /> List this for sale
              </Button>
            </div>
          )}

          {data.can_edit && (
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Source it instead</p>
            <p className="mt-1 text-xs text-slate-600">Post what you want and let suppliers come back with quotes.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div><Label>What do you need?</Label><Input data-testid="fashion-source-title" value={source.title} onChange={(e) => setSource({ ...source, title: e.target.value })} placeholder="500 lined hi-vis gloves, size L" /></div>
              <div>
                <Label>Segment</Label>
                <select data-testid="fashion-source-segment" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                  value={source.segment} onChange={(e) => setSource({ ...source, segment: e.target.value })}>
                  {data.segment_options.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
                </select>
              </div>
              <div><Label>How many</Label><Input data-testid="fashion-source-qty" type="number" value={source.quantity} onChange={(e) => setSource({ ...source, quantity: e.target.value })} /></div>
              <div><Label>Budget (optional)</Label><Input data-testid="fashion-source-budget" type="number" value={source.budget} onChange={(e) => setSource({ ...source, budget: e.target.value })} /></div>
              <div>
                <Label>Currency</Label>
                <select data-testid="fashion-source-currency" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                  value={source.currency} onChange={(e) => setSource({ ...source, currency: e.target.value })}>
                  {(data.currencies || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
                </select>
              </div>
            </div>
            <Button variant="outline" className="mt-3 w-full sm:w-auto" data-testid="fashion-source-submit" onClick={askSuppliers}
              disabled={source.title.trim().length < 3}>
              <Megaphone size={16} className="mr-1.5" /> Ask suppliers to quote
            </Button>
          </div>
          )}

          {data.for_sale?.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fashion-for-sale">
              {data.for_sale.map((l, i) => (
                <div key={l.listing_id} data-testid={`fashion-for-sale-${i}`} className="border border-border bg-white p-4">
                  {l.image_url && <img src={`${BACKEND}${l.image_url}`} alt={l.title} className="mb-3 h-32 w-full border border-border object-cover" />}
                  <p className="font-semibold text-[#0F172A]">{l.title}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">{LABEL(l.segment || "")} · {l.quantity} {l.unit} left</p>
                  <p className="mt-2 text-lg font-bold text-[#0B5C2E]">{l.currency} {l.price}</p>
                  <div className="mt-3">
                    <ShareBar url={`${BACKEND}/api/share/fashion/listing/${l.listing_id}`}
                      text={`${l.title} — ${l.currency} ${l.price}`}
                      testid={`fashion-listing-share-${i}`} compact instagram />
                  </div>
                  {!l.image_url && data.can_sell && (
                    <button data-testid={`fashion-listing-picture-${i}`} onClick={() => listingPicture(l.listing_id)} disabled={busy}
                      className="mt-3 inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60">
                      <ImageIcon size={14} /> {busy ? "Drawing…" : "Make a picture for this"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "casting" && (user?.role === "admin" || user?.role === "manager") && (
        <CastingBoard segments={data.segment_options} />
      )}

      {view === "board" && (
        <>
        <CastingStrip onOpen={() => setView("casting")} />
        {data.segments.length === 0 ? (
          <EmptyState testid="fashion-empty" title="No apparel buying yet"
            description="Once you raise purchase orders for workwear, uniforms, footwear, PPE or fabric, this board shows what is moving and what is running low." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fashion-segments">
            {data.segments.map((row, i) => (
              <div key={row.segment} data-testid={`fashion-segment-${i}`} className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-[#0F172A]">{LABEL(row.segment)}</p>
                  <span className={`flex items-center gap-1 text-sm font-semibold ${
                    row.trend_pct == null ? "text-slate-400" : row.trend_pct > 0 ? "text-[#0B5C2E]" : "text-[#B3241B]"}`}
                    data-testid={`fashion-trend-${i}`}>
                    {row.trend_pct != null && (row.trend_pct > 0 ? <TrendUp size={14} /> : <TrendDown size={14} />)}
                    {pct(row.trend_pct)}
                  </span>
                </div>
                <p className="mt-3 text-2xl font-bold text-[#0F172A]">{row.recent_units.toLocaleString()}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">units bought, last 3 months</p>
                <p className="mt-2 text-xs text-slate-600">{row.order_lines} order line(s) · {row.products.slice(0, 3).join(", ")}</p>
                {row.low_stock.length > 0 && (
                  <p className="mt-2 border-l-2 border-[#B3241B] bg-[#FFF1F0] px-2 py-1 text-xs text-[#8E1C15]" data-testid={`fashion-low-${i}`}>
                    Low on {row.low_stock.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        </>
      )}

      {view === "notes" && (
        <div className="space-y-4" data-testid="fashion-notes">
          {data.can_edit && (
            <div className="border border-border bg-white p-4">
              <p className="text-sm font-semibold text-[#0F172A]">Add a trend note</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div><Label>Title</Label><Input data-testid="fashion-note-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Hi-vis softshells selling early" /></div>
                <div>
                  <Label>Segment</Label>
                  <select data-testid="fashion-note-segment" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                    value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })}>
                    {data.segment_options.map((s) => <option key={s} value={s}>{LABEL(s)}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2"><Label>What you are seeing</Label><Textarea data-testid="fashion-note-body" rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="tap-safe flex items-center gap-2 text-sm text-slate-600">
                  <input data-testid="fashion-note-public" type="checkbox" className="h-5 w-5 shrink-0" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} />
                  Show on the public trends page
                </label>
                <Button data-testid="fashion-note-save" className="w-full sm:w-auto" onClick={addNote} disabled={form.title.trim().length < 3 || form.body.trim().length < 3}>
                  <Plus size={16} className="mr-1.5" /> Save the note
                </Button>
              </div>
            </div>
          )}

          {data.notes.length === 0 ? (
            <EmptyState testid="fashion-notes-empty" title="No trend notes yet"
              description="Jot down what you are seeing on the floor — early demand, colours customers ask for, fabrics that keep selling out." />
          ) : (
            data.notes.map((n, i) => (
              <div key={n.id} data-testid={`fashion-note-${i}`} className="border border-border bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{n.title}</p>
                    <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                      {LABEL(n.segment || "")} · {n.author} · {(n.created_at || "").slice(0, 10)}
                      {n.public ? " · public" : " · internal"}
                    </p>
                  </div>
                  {(user?.role === "admin" || n.author === user?.name) && (
                    <button data-testid={`fashion-note-remove-${i}`} onClick={() => removeNote(n.id)}
                      className="border border-border px-2 py-1 text-xs text-[#B3241B] hover:bg-[#FFECEB]">
                      <Trash size={12} />
                    </button>
                  )}
                </div>
                <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{n.body}</p>
                {n.image_url && (
                  <img src={`${BACKEND}${n.image_url}`} alt={n.title}
                    data-testid={`fashion-note-img-${i}`}
                    className="mt-3 w-full max-w-xs border border-border" />
                )}
                {user?.role === "admin" && (
                  <label className="tap-safe mt-3 flex items-center gap-2 text-xs text-slate-600">
                    <input data-testid={`fashion-note-auto-${i}`} type="checkbox" className="h-5 w-5 shrink-0"
                      checked={!!n.picture_every_5m} onChange={(e) => toggleAuto(n.id, e.target.checked)} />
                    Fresh picture every 5 minutes
                  </label>
                )}
                {n.public && (
                  <div className="mt-3">
                    <ShareBar url={`${BACKEND}/api/share/fashion/note/${n.id}`}
                      text={n.title} testid={`fashion-note-share-${i}`} compact instagram />
                  </div>
                )}
                {!n.image_url && (user?.role === "admin" || n.author === user?.name) && (
                  <button data-testid={`fashion-note-picture-${i}`} onClick={() => notePicture(n.id)} disabled={busy}
                    className="mt-3 inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60">
                    <ImageIcon size={14} /> {busy ? "Drawing…" : "Make a picture for this"}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
